import React, { useMemo, useState } from "react";
import { Section } from "@design-system/Section/Section";
import { useWorkspaceEmailAliases } from "@api/workspaceEmailAlias.api";
import { useEntityEmailAddressesForEntities } from "@api/entity.api";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { WorkspaceEmailAliasKind } from "@common/constants/domains/receipts/constants";
import { formatWorkspaceEmailAliasAddress } from "@common/helpers/workspace-email-alias";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useCanShowBillInvoiceInbox } from "../useCanShowBillInvoiceInbox";
import { EmailAliasesView } from "./EmailAliasesView";
import { EmailAliasRow } from "./emailAliasesViewConfig";
import { AddEmailAliasModal } from "./AddEmailAliasModal";
import { AddEntityCounterpartyEmailAliasModal } from "./AddEntityCounterpartyEmailAliasModal";
import { buildDefaultEmailAliasRows } from "./buildDefaultEmailAliasRows";
import { getEmailAliasSectionCopy } from "./emailAliasSectionCopy";

interface EmailAliasesSectionProps {
    kind: WorkspaceEmailAliasKind;
}

export const EmailAliasesSection: React.FC<EmailAliasesSectionProps> = ({
    kind,
}) => {
    const { data: aliases, isLoading } = useWorkspaceEmailAliases();
    const workspace = useActiveWorkspace();
    const [search, setSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const canShowBillInvoiceInbox = useCanShowBillInvoiceInbox();
    const { draftsTab } = useTypedFlags();
    const showsDefaultEntityEmails = kind !== "receipts";
    const entityContexts = useEntitiesContexts({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const businessEntities = useMemo(
        () => entityContexts?.map((context) => context.entity) ?? [],
        [entityContexts],
    );
    const entityEmailAddressQueries = useEntityEmailAddressesForEntities(
        businessEntities.map((entity) => entity.id),
        { enabled: showsDefaultEntityEmails },
    );
    const isEntityScopedAliasForm = kind !== "receipts" && draftsTab;
    const copy = getEmailAliasSectionCopy({
        kind,
        canShowBillInvoiceInbox,
    });

    const counterpartyIds = useMemo(() => {
        if (!aliases) {
            return [];
        }
        return [
            ...new Set(
                aliases.flatMap((alias) =>
                    alias.kind === kind && alias.counterpartyId
                        ? [alias.counterpartyId]
                        : [],
                ),
            ),
        ];
    }, [aliases, kind]);

    const counterparties = useCounterpartiesQuery(
        {
            counterpartiesIds: counterpartyIds,
            includeRegistered: RegisteredCounterpartyIncludeScope.ALL,
        },
        { enabled: counterpartyIds.length > 0 },
    );

    const counterpartyNamesById = useMemo(() => {
        const names = new Map<string, string>();
        for (const row of counterparties.data?.data ?? []) {
            names.set(row.counterparty.id, row.counterparty.name);
        }
        return names;
    }, [counterparties.data]);

    const areDefaultEmailsLoading =
        showsDefaultEntityEmails &&
        (entityContexts === undefined ||
            entityEmailAddressQueries.some((query) => query.isPending));

    const tableData = useMemo<EmailAliasRow[] | null>(() => {
        if (isLoading || areDefaultEmailsLoading) {
            return null;
        }
        if (!aliases) {
            return [];
        }

        const customRows = aliases
            .filter((alias) => alias.kind === kind)
            .filter((alias) =>
                formatWorkspaceEmailAliasAddress({
                    emailLocalPart: alias.emailLocalPart,
                    kind: alias.kind,
                })
                    .toLowerCase()
                    .includes(search.toLowerCase()),
            )
            .map((alias) => ({
                ...alias,
                id: `${alias.kind}:${alias.emailLocalPart}`,
                counterpartyName: alias.counterpartyId
                    ? (counterpartyNamesById.get(alias.counterpartyId) ??
                      "Linked vendor")
                    : null,
                isDefault: false,
            }));

        if (kind === "receipts") {
            return customRows;
        }

        const defaultRows = buildDefaultEmailAliasRows({
            kind,
            search,
            workspaceId: workspace?.id ?? "",
            entities: businessEntities.map((entity, index) => ({
                id: entity.id,
                slug: entityEmailAddressQueries[index]?.data?.slug ?? null,
            })),
        });

        return [...defaultRows, ...customRows];
    }, [
        aliases,
        areDefaultEmailsLoading,
        businessEntities,
        counterpartyNamesById,
        entityEmailAddressQueries,
        isLoading,
        kind,
        search,
        workspace?.id,
    ]);

    return (
        <Section heading={copy.heading} subheading={copy.subheading}>
            <TablePageHeader search={search} onSearchChange={setSearch}>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => setShowAddModal(true)}
                >
                    Add email
                </Button>
            </TablePageHeader>

            <EmailAliasesView
                data={tableData}
                kind={kind}
                emptyBody={copy.emptyBody}
            />

            {isEntityScopedAliasForm ? (
                <AddEntityCounterpartyEmailAliasModal
                    show={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    kind={kind}
                />
            ) : (
                <AddEmailAliasModal
                    show={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    kind={kind}
                />
            )}
        </Section>
    );
};
